import { useCallback, useMemo, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Typography } from 'antd'
import { ArrowLeft, FileSearch } from 'lucide-react'
import { getDefaultPreset } from '@bandos/shared'
import {
  useNodeUtils,
  useReportHierarchy,
  useSelectionFromStorage,
  ReportContent,
  SelectionBanner,
  type ReenterSelectionState,
} from '../../features/report'
import { useSectionVisibility } from '../../features/reportSections'
import { useMindmapData } from '../ReportPage/hooks/useMindmapData'
import { useValidationMethod } from './useValidationMethod'
import { useBatchValidation } from './useBatchValidation'
import { useCreateValidationNode } from './useCreateValidationNode'
import { MethodSelectionPanel } from './MethodSelectionPanel'
import { BatchQuestionReview } from '../../features/validation'
import { SkeletonReport } from '../../features/skeleton'
import {
  PageLayout,
  ReportPanel,
  ReportPanelHeader,
  BackButton,
  PageTitle,
  SelectionBannerWrapper,
  MethodPanel,
  EmptyReport,
} from './ValidationPlannerPage.styles'

const { Text } = Typography

const NO_OP_RENDER = (): null => null

export function ValidationPlannerPage() {
  const { id: mindmapId } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const { loading, mindmap, projectLabels, nodeLabelMap } = useMindmapData(mindmapId)

  const storageKey = mindmapId ? `validate-selection-${mindmapId}` : undefined
  const { selectionIds } = useSelectionFromStorage(storageKey)

  const { isVisible } = useSectionVisibility()

  // Visibility: show only selected nodes (fall back to all if no selection)
  const effectiveShouldDisplayNode = useCallback(
    (nodeId: string): boolean => {
      if (selectionIds) return selectionIds.has(nodeId)
      return true
    },
    [selectionIds]
  )

  const nodes = useMemo(() => mindmap?.content.nodes ?? [], [mindmap?.content.nodes])
  const edges = useMemo(() => mindmap?.content.edges ?? [], [mindmap?.content.edges])

  const { getEffectiveNodeType, getEffectiveNodeData, nodeMap, getChildIdeas, getLabelsForNode } =
    useNodeUtils(nodes, edges, projectLabels, nodeLabelMap, effectiveShouldDisplayNode)

  const hierarchy = useReportHierarchy(
    nodes,
    edges,
    nodeMap,
    getEffectiveNodeType,
    effectiveShouldDisplayNode,
    getChildIdeas,
    isVisible
  )

  const { selectedMethod, setSelectedMethod, methodLabel } = useValidationMethod()

  const [targetDurationMinutes, setTargetDurationMinutes] = useState<number>(
    getDefaultPreset('survey').minutes
  )
  // Empty string = auto-detect from venture description (no language sent to backend).
  const [language, setLanguage] = useState<string>('')

  const handleMethodSelect = useCallback(
    (method: typeof selectedMethod) => {
      setSelectedMethod(method)
      if (method) {
        setTargetDurationMinutes(getDefaultPreset(method).minutes)
      }
    },
    [setSelectedMethod]
  )

  const categoryCounts = useMemo(() => {
    const counts = new Map<string, number>()
    if (!selectionIds) return counts
    for (const nodeId of selectionIds) {
      const node = nodes.find((n) => n.id === nodeId)
      if (!node) continue
      counts.set(node.data.category, (counts.get(node.data.category) ?? 0) + 1)
    }
    return counts
  }, [selectionIds, nodes])

  const handleBack = useCallback(() => {
    if (mindmapId) {
      void navigate(`/mindmap/${mindmapId}`)
    } else {
      void navigate(-1)
    }
  }, [navigate, mindmapId])

  const handleChangeSelection = useCallback(() => {
    if (!mindmapId) return
    const reenterSelection: ReenterSelectionState = {
      nodeIds: selectionIds ? [...selectionIds] : [],
      storageKey: `validate-selection-${mindmapId}`,
      destination: `/mindmap/${mindmapId}/validate`,
      title: 'What do you want to test?',
      subtitle: 'Pick the needs and ideas you want to test',
    }
    void navigate(`/mindmap/${mindmapId}`, { state: { reenterSelection } })
  }, [navigate, mindmapId, selectionIds])

  const { phase, questions, nodeMetadata, generate, reset } = useBatchValidation()
  const handleCreate = useCreateValidationNode({
    mindmapId,
    method: selectedMethod,
    nodes: mindmap?.content.nodes,
    selectionIds,
  })

  const handleContinue = useCallback(() => {
    if (!mindmapId || !selectedMethod || !selectionIds || selectionIds.size === 0) return
    void generate(
      mindmapId,
      selectedMethod,
      [...selectionIds],
      targetDurationMinutes,
      language || undefined
    )
  }, [mindmapId, selectedMethod, selectionIds, generate, targetDurationMinutes, language])

  if (loading) {
    return <SkeletonReport />
  }

  if (!mindmap) {
    return (
      <EmptyReport>
        <Text type="secondary">Mindmap not found</Text>
      </EmptyReport>
    )
  }

  const hasContent =
    hierarchy.ventureNodes.length > 0 ||
    hierarchy.standaloneNeeds.length > 0 ||
    hierarchy.standaloneIdeas.length > 0

  return (
    <PageLayout>
      <ReportPanel>
        <ReportPanelHeader>
          <BackButton onClick={handleBack}>
            <ArrowLeft size={14} strokeWidth={2} />
            Back
          </BackButton>
          <PageTitle>{mindmap.title}</PageTitle>
        </ReportPanelHeader>

        <SelectionBannerWrapper>
          <SelectionBanner
            categoryCounts={categoryCounts}
            onChangeSelection={handleChangeSelection}
          />
        </SelectionBannerWrapper>

        {hasContent ? (
          <ReportContent
            ventureNodes={hierarchy.ventureNodes}
            standaloneNeeds={hierarchy.standaloneNeeds}
            standaloneIdeas={hierarchy.standaloneIdeas}
            getEffectiveNodeData={getEffectiveNodeData}
            getVentureNeeds={hierarchy.getVentureNeeds}
            getChildIdeas={getChildIdeas}
            getLabelsForNode={getLabelsForNode}
            renderIdeaExtras={NO_OP_RENDER}
            isVisible={isVisible}
            getAllIdeasUnderVenture={hierarchy.getAllIdeasUnderVenture}
            ideasFromStandaloneNeeds={hierarchy.ideasFromStandaloneNeeds}
          />
        ) : (
          <EmptyReport>
            <FileSearch size={48} color="var(--color-text-tertiary)" />
            <Text type="secondary">No nodes to display</Text>
          </EmptyReport>
        )}
      </ReportPanel>

      <MethodPanel>
        {phase === 'review' && questions && nodeMetadata && selectedMethod ? (
          <BatchQuestionReview
            questions={questions}
            nodeMetadata={nodeMetadata}
            method={selectedMethod}
            onBack={reset}
            onCreate={(batch) => void handleCreate(batch)}
          />
        ) : (
          <MethodSelectionPanel
            selectedMethod={selectedMethod}
            methodLabel={methodLabel}
            isGenerating={phase === 'generating'}
            selectedDurationMinutes={targetDurationMinutes}
            selectedLanguage={language}
            selectionCount={selectionIds?.size ?? 0}
            onSelect={handleMethodSelect}
            onDurationChange={setTargetDurationMinutes}
            onLanguageChange={setLanguage}
            onCancel={handleBack}
            onContinue={handleContinue}
          />
        )}
      </MethodPanel>
    </PageLayout>
  )
}
